"use client";
import { type Dispatch, type SetStateAction, useState } from "react";
import { toast } from "react-toastify";
import { api } from "@/utils/api";
import { Button } from "@/components/ui/button"
import { useEffect } from "react"
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog"

import { Label } from "@/components/ui/label"
import { Loader2, Plus } from "lucide-react";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import { useSession } from "@/lib/auth-client";
import { userIsSuperAdmin } from "@/utils/accessControl";

export const CreateCustomerDialog = () => {
    const [open, setOpen] = useState(false);
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button>
                    <Plus className="mr-2 h-4 w-4" />
                    <span>
                        New customer
                    </span>
                </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>New customer</DialogTitle>
                </DialogHeader>
                <CreateCustomerForm setOpen={setOpen} />
            </DialogContent>
        </Dialog>
    )
}


const CreateCustomerForm = ({ setOpen }: { setOpen: Dispatch<SetStateAction<boolean>> }) => {
    const [name, setName] = useState('');
    const [subscriptionActive, setSubscriptionActive] = useState(true);
    const [internal, setInternal] = useState(false);
    const utils = api.useUtils();
    const { isPending, mutateAsync, error: mutateError } = api.customer.create.useMutation({
        onSuccess: async () => {
            setOpen(false)
            await utils.customer.getAll.invalidate();
            await utils.customer.getAllForUser.invalidate();
        },
    });
    const { data: session } = useSession();

    useEffect(() => {
        if (mutateError?.data?.code === "BAD_REQUEST" && !mutateError?.data?.zodError) {
            toast.error(mutateError.message);
        }
    }, [mutateError]);

    const handleCreate = (e: React.SyntheticEvent) => {
        e.preventDefault()
        void toast.promise(
            mutateAsync({ name: name, netsuite_id: null, subscription_active: subscriptionActive, internal }),
            {
                success: 'Customer created successfully',
            }
        )
    }
    if (!session) return null;
    return (
        <form onSubmit={handleCreate}>
            <div className="grid gap-6 p-4">
                <div className="space-y-3">
                    <Label htmlFor="name">
                        Customer name
                    </Label>
                    <Input id="name" className="my-1" placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} />
                    {mutateError?.data?.zodError?.fieldErrors.name && <div className="col-span-4 text-sm text-destructive text-right">Name is required</div>}
                </div>
                <div className="flex gap-3 flex-nowrap">
                    <Label htmlFor="subscription">
                        DSM subscription is active
                    </Label>
                    <Checkbox id="subscription" checked={subscriptionActive} onCheckedChange={(checked: boolean) => setSubscriptionActive(checked)} disabled />
                </div>
                {
                    !userIsSuperAdmin(session) ? null :
                        <div className="flex gap-3 flex-nowrap">
                            <Label htmlFor="internal">
                                Internal customer
                            </Label>
                            <Checkbox id="internal" checked={internal} onCheckedChange={(checked: boolean) => setInternal(checked)} />
                        </div>
                }

                <DialogFooter className="grid grid-cols-4">
                    <div></div>
                    <div></div>
                    <DialogClose asChild>
                        <Button variant="outline">
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button onClick={handleCreate} disabled={isPending}>
                        {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        <span>
                            Save
                        </span>
                    </Button>
                </DialogFooter>
            </div>
        </form>
    );
};